<template>
  <div
    :class="isMobile ? 'tab-content-card--mobile' : 'tab-content-card'"
  >
    <v-card-title-wrapper
      v-if="config.title"
      class="v-card-title"
    >
      {{ config.title }}
    </v-card-title-wrapper>

    <div
      :class="isMobile ? 'v-card-text--mobile' : 'v-card-text'"
    >
      <v-row-wrapper
        v-for="(day, index) in modifiedFormData[dataKey]"
        :key="index"
        class="working-hours-row"
        align="start"
        no-gutters
      >
        <v-col-wrapper
          cols="12"
          md="2"
          class="d-flex align-center working-hours-row__day"
        >
          <image-checkbox
            :value="day.enabled"
            :config="{ label: dayLabels[index] }"
            :disabled="!canEdit"
            @input="value => onCheckboxInput(index, value)"
          />
        </v-col-wrapper>

        <v-col-wrapper
          cols="12"
          md="4"
          class="working-hours-row__range"
          @dblclick="unlockFieldsOnDblClick"
        >
          <datetime-picker-field
            v-model="day.from"
            :disabled="!day.enabled || !canEdit"
            :edit-mode="edit"
            :create-mode="createMode"
            :append-icon="timeAppendIcon"
            :display-format="timeFormat"
            :value-format="timeFormat"
            :picker-field="timePickerField"
            :text-field-props="{ clearable: true }"
            :validation-errors="validationErrors"
            :custom-field-key="dataKey + '.' + index + '.work_start'"
            :update-data="updateData"
            :update-validation-errors="updateValidationErrors"
            class="working-hours-row__time-field"
            @clear-validation-errors="$emit('clear-validation-errors', dataKey + '.' + index + '.work_start')"
          />

          <span class="time-range-separator">
            {{ translate('road-maintenance/temporary_regulation/crews.to') }}
          </span>

          <datetime-picker-field
            v-model="day.to"
            :disabled="!day.enabled || !canEdit"
            :edit-mode="edit"
            :create-mode="createMode"
            :append-icon="timeAppendIcon"
            :display-format="timeFormat"
            :value-format="timeFormat"
            :picker-field="timePickerField"
            :text-field-props="{ clearable: true }"
            :validation-errors="validationErrors"
            :custom-field-key="dataKey + '.' + index + '.work_end'"
            :update-data="updateData"
            :update-validation-errors="updateValidationErrors"
            class="working-hours-row__time-field"
            @clear-validation-errors="$emit('clear-validation-errors', dataKey + '.' + index + '.work_end')"
          />
        </v-col-wrapper>

        <v-col-wrapper
          cols="12"
          md="1"
          class="d-flex align-center justify-end break-label pr-2"
        >
          {{ translate('road-maintenance/temporary_regulation/crews.break') }}
        </v-col-wrapper>

        <v-col-wrapper
          cols="12"
          md="4"
          class="working-hours-row__range"
          @dblclick="unlockFieldsOnDblClick"
        >
          <datetime-picker-field
            v-model="day.break_from"
            :disabled="!day.enabled || !canEdit"
            :edit-mode="edit"
            :create-mode="createMode"
            :append-icon="timeAppendIcon"
            :display-format="timeFormat"
            :value-format="timeFormat"
            :picker-field="timePickerField"
            :text-field-props="{ clearable: true }"
            :validation-errors="validationErrors"
            :custom-field-key="dataKey + '.' + index + '.break_start'"
            :update-data="updateData"
            :update-validation-errors="updateValidationErrors"
            class="working-hours-row__time-field"
            @clear-validation-errors="$emit('clear-validation-errors', dataKey + '.' + index + '.break_start')"
          />

          <span class="time-range-separator">
            {{ translate('road-maintenance/temporary_regulation/crews.to') }}
          </span>

          <datetime-picker-field
            v-model="day.break_to"
            :disabled="!day.enabled || !canEdit"
            :edit-mode="edit"
            :create-mode="createMode"
            :append-icon="timeAppendIcon"
            :display-format="timeFormat"
            :value-format="timeFormat"
            :picker-field="timePickerField"
            :text-field-props="{ clearable: true }"
            :validation-errors="validationErrors"
            :custom-field-key="dataKey + '.' + index + '.break_end'"
            :update-data="updateData"
            :update-validation-errors="updateValidationErrors"
            class="working-hours-row__time-field"
            @clear-validation-errors="$emit('clear-validation-errors', dataKey + '.' + index + '.break_end')"
          />
        </v-col-wrapper>
      </v-row-wrapper>
    </div>
  </div>
</template>

<script setup>
import Vue, { computed, watch } from 'vue'
import ImageCheckbox from '@/global/components/fields/ImageCheckbox.vue'
import DatetimePickerField from '@/global/components/fields/DatetimePickerField.vue'
import VRowWrapper from '@/global/components/vuetifyComponentsWrappers/VRowWrapper.vue'
import VColWrapper from '@/global/components/vuetifyComponentsWrappers/VColWrapper.vue'
import VCardTitleWrapper from '@/global/components/vuetifyComponentsWrappers/VCardTitleWrapper.vue'
import { useTranslate } from '@/global/composables/useTranslate'
import { useAppContext } from '@/global/services/compositionApi/useAppContext'
import { FORMATS } from '@/global/services/date'

const DEFAULT_DATA_KEY = 'working_hours'
const WORK_DAYS_COUNT = 5

const props = defineProps({
  config: {
    type: Object,
    default: () => ({})
  },
  formData: {
    type: Object,
    default: () => ({})
  },
  edit: {
    type: Boolean,
    default: false
  },
  createMode: {
    type: Boolean,
    default: false
  },
  validationErrors: {
    type: Object,
    default: () => ({})
  },
  updateData: {
    type: Function,
    default: () => {}
  },
  updateValidationErrors: {
    type: Function,
    default: () => {}
  }
})

const emit = defineEmits([
  'clear-validation-errors',
  'set-edit'
])

const { translate } = useTranslate()
const { vuetify } = useAppContext()

const timeFormat = FORMATS.timeShort
const timeAppendIcon = 'mdi-clock-time-four-outline'
const timePickerField = { subType: 'time', format: timeFormat }

const modifiedFormData = computed(() => props.formData)

const dataKey = computed(() => props.config?.dataKey || DEFAULT_DATA_KEY)

const canEdit = computed(() => props.edit || props.createMode)

const isMobile = computed(() => !!vuetify?.breakpoint?.smAndDown)

const dayLabels = computed(() => ([
  translate('base.monday'),
  translate('base.tuesday'),
  translate('base.wednesday'),
  translate('base.thursday'),
  translate('base.friday'),
  translate('base.saturday'),
  translate('base.sunday')
]))

const defaultWorkingHours = computed(() => dayLabels.value.map((label, index) => ({
  day: index,
  enabled: index < WORK_DAYS_COUNT,
  from: null,
  to: null,
  break_from: null,
  break_to: null
})))

const syncWorkingHours = () => {
  if (!modifiedFormData.value) {
    return
  }

  const backendHours = Array.isArray(modifiedFormData.value[dataKey.value]) ? modifiedFormData.value[dataKey.value] : []

  Vue.set(modifiedFormData.value, dataKey.value, defaultWorkingHours.value.map(defaultDay => {
    const found = backendHours.find(day => parseInt(day.day) === defaultDay.day)
    return { ...defaultDay, ...(found || {}) }
  }))
}

watch(() => props.formData, syncWorkingHours, { immediate: true })

const onCheckboxInput = (index, value) => {
  if (!canEdit.value) {
    return
  }
  Vue.set(modifiedFormData.value[dataKey.value][index], 'enabled', value)

  // Turning a day off disables (and stops sending) its time fields, so any
  // "time is required" errors the backend returned for that day are moot -
  // the key prefix clears all four of them at once.
  if (!value) {
    emit('clear-validation-errors', `${dataKey.value}.${index}`)
  }
}

// Double-clicking a time field while viewing (not yet editing) turns on edit
// mode for the whole tab, same as the generic dynamic-columns/fields-renderer
// blocks elsewhere in the app.
const unlockFieldsOnDblClick = () => {
  if (!props.createMode) {
    emit('set-edit', true)
  }
}
</script>

<style scoped lang="scss">
// Height of a dense outlined field's input box. The row is anchored to the top
// (align="start") so a field's validation message hangs below it without
// pushing its siblings down; the short elements (day checkbox, "Pauza"/"Do"
// labels) are each centered within a box of this height so they stay aligned
// with the input boxes regardless of any message.
$field-height: 40px;

.tab-content-card > *:last-child {
  border-bottom-right-radius: 16px !important;
  border-bottom-left-radius: 16px !important;
  margin-bottom: 10px;
}

.tab-content-card--mobile {
  width: 100%;
  margin: 0 !important;
}

.v-card-title {
  font-family: 'Satoshi', sans-serif;
  font-size: 20px;
  font-weight: 700;
  line-height: 28px;
  color: #111827;
  margin-top: 1%;
}

.v-card-text {
  padding: 16px;
  margin: 0 15px;
  border: 1px solid #E5E7EB;
  border-radius: 16px;
}

.v-card-text--mobile {
  padding: 16px;
  margin-left: 1%;
  margin-right: 1%;
}

.working-hours-row {
  margin-bottom: 12px;
}

.working-hours-row__day {
  cursor: pointer;
  padding-right: 12px;
  min-height: $field-height;
}

.break-label {
  font-family: 'Satoshi', sans-serif;
  font-size: 14px;
  color: #111827;
  line-height: 20px;
  font-weight: 500;
  padding: 0 20px 0 12px;
  min-height: $field-height;
}

.working-hours-row__range {
  // Grid instead of flex: a field with a value renders an extra clearable
  // "x" icon inside its own bordered box alongside the clock icon, while an
  // empty sibling only renders the clock - that extra icon's intrinsic
  // content width was throwing off `flex: 1 1 0`'s equal split. `minmax(0, 1fr)`
  // columns are sized purely from the container's available space, with no
  // possibility of a child's content pushing its own column wider or narrower.
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
  // Top-anchor the fields and the "Do" separator: a validation message under
  // one field then extends the row downward instead of re-centering the row and
  // shifting the message-less field / separator out of line with it.
  align-items: start;
  gap: 8px;
  padding-right: 12px;
}

.working-hours-row__time-field {
  min-width: 0;
}

.time-range-separator {
  font-family: 'Satoshi', sans-serif;
  font-size: 14px;
  color: #111827;
  // Match the input box height so the top-anchored "Do" stays vertically
  // centered on the field, with or without a validation message below it.
  line-height: $field-height;
  font-weight: 500;
  flex: 0 0 auto;
}
</style>
